import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import {
  useEditOrganizationGroupMutation,
  useEditOrganizationGroupVehiclesMutation,
  useEditOrganizationGroupUsersMutation,
  useOrganizationUsersFilterableQuery,
  useOrganizationVehiclesFilterableQuery,
  useSingleOrganizationGroupQuery,
  useSingleOrganizationGroupUsersQuery,
  useSingleOrganizationGroupVehiclesQuery,
} from "../../../api/network/adminApiServices";
import { LegacyOrganizationAccount } from "../../../api/types/Account";
import {
  SingleOrganizationGroupPayload,
  TEditOrganizationGroupPayloadData,
  TEditOrganizationGroupVehiclesPayloadData,
  TEditOrganizationGroupUsersPayloadData,
} from "../../../api/types/Group";
import {
  LegacyVehicleEntityFilterableListingPayload,
  TLegacyVehicle,
} from "../../../api/types/Vehicle";
import {
  LegacyUserEntityFilterableListingPayload,
  TLegacyUser,
} from "../../../api/types/User";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../../components/admin/formFields";
import DualListboxPagedLHS from "../../../components/dualListboxPagedLHS";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TCustomizableItemList } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData } from "../../../utils/user";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";

type TGroupEditFormFieldsProps = {
  groupId: string;
  loadingState: boolean; // to show any loading state happening outside this component
  isNewEntity: React.MutableRefObject<boolean>;
  onLoadingStateChangeEditGroup?: (value: boolean) => void;
  onLoadingStateChangeSingleGroup?: (value: boolean) => void;
  onLoadingStateChangeSingleGroupVehicles?: (value: boolean) => void;
  onUpdateError: (error: any) => void;
  onUpdateSuccess: () => void;
  onCancel?: () => void;
  setShowInactiveVehicles: (value: boolean) => void;
  setShowInactiveUsers: (value: boolean) => void;
  setUserCanEdit: (value: boolean) => void;
  showGroupIdField?: boolean;
  showInactiveVehicles: boolean;
  showInactiveUsers: boolean;
  userCanEdit: boolean;
  viewOnlyMode: boolean;
  renderedOnModal?: boolean;
};

const GroupEditFormFields: FC<TGroupEditFormFieldsProps> = ({
  groupId,
  loadingState,
  isNewEntity,
  onLoadingStateChangeEditGroup,
  onLoadingStateChangeSingleGroup,
  onLoadingStateChangeSingleGroupVehicles,
  onUpdateError,
  onUpdateSuccess,
  onCancel,
  setShowInactiveVehicles,
  setShowInactiveUsers,
  setUserCanEdit,
  showGroupIdField = true,
  showInactiveVehicles,
  showInactiveUsers,
  userCanEdit,
  viewOnlyMode,
  renderedOnModal = false,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.groups.detailsPage",
  });
  const navigate = useNavigate();

  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";
  // search text for right side
  // const [rightSearchText, setRightSearchText] = React.useState<string>("");

  // account level asset search fields
  const accountDataInStore = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );
  const assetSearchFields = accountDataInStore?.asset_searchable_fields
    ? accountDataInStore.asset_searchable_fields.split(",") // .map((field) => field.trim().toLowerCase())
    : [];

  // current description and group mechainism
  const [currentDescription, setCurrentDescription] =
    React.useState<string>("");

  // initial right list data
  const [initialRightListData, setInitialRightListData] = React.useState<
    TCustomizableItemList[]
  >([]);
  const [rightListItems, setRightListItems] = React.useState<
    TCustomizableItemList[]
  >([]); // initialRightListData

  // prepare query params for fetching organization groups
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgVehiclesQueryParams, setOrgVehiclesQueryParams] =
    React.useState<LegacyVehicleEntityFilterableListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LHS_RHS_PAGE_SIZE,
      search: "",
      isactive: showInactiveVehicles ? undefined : 1,
      exclude_ids: rightListItems.map((item) => item.id),
      //isactive: "both" as FilterType // not needed as both active and inactive vehicles are needed
    });

  // update isactive filter when showInactiveVehicles changes
  useEffect(() => {
    setOrgVehiclesQueryParams({
      ...orgVehiclesQueryParams,
      isactive: showInactiveVehicles ? undefined : 1,
    });
  }, [showInactiveVehicles]);

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setOrgVehiclesQueryParams({
      ...orgVehiclesQueryParams,
      search: value,
      page: 1,
    });
  }, 500);

  // ---------------------------------------------------------------------------
  // Users section (mirrors the vehicles section above)
  // ---------------------------------------------------------------------------
  // right (in-group) users list
  const [initialRightUserListData, setInitialRightUserListData] =
    React.useState<TCustomizableItemList[]>([]);
  const [rightUserListItems, setRightUserListItems] = React.useState<
    TCustomizableItemList[]
  >([]);

  // query params for fetching available (not-in-group) users. We pass
  // exclude_all_groups_users so users that can see all groups (no GroupList
  // rows) never appear in the picker.
  const [orgUsersQueryParams, setOrgUsersQueryParams] =
    React.useState<LegacyUserEntityFilterableListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LHS_RHS_PAGE_SIZE,
      search: "",
      isactive: showInactiveUsers ? undefined : 1,
      exclude_ids: rightUserListItems.map((item) => item.id),
      exclude_all_groups_users: true,
    });

  // update isactive filter when showInactiveUsers changes
  useEffect(() => {
    setOrgUsersQueryParams({
      ...orgUsersQueryParams,
      isactive: showInactiveUsers ? undefined : 1,
    });
  }, [showInactiveUsers]);

  const debouncedSetUserSearchKeyword = useDebouncedCallback(
    (value: string) => {
      setOrgUsersQueryParams({
        ...orgUsersQueryParams,
        search: value,
        page: 1,
      });
    },
    500
  );

  // fetch single group details
  const {
    data: dataSingleGroup,
    isFetching: isFetchingSingleGroup,
    // the single group API does not respond with error 404, so we need to handle it by checking if dataSingleGroup?.results is empty
    // isError: isErrorSingleEntity,
    // error: errorSingleEntity,
  } = useSingleOrganizationGroupQuery(
    {
      accountid: thisUserOrganizationId,
      groupid: groupId,
    } as SingleOrganizationGroupPayload,
    {
      skip: !groupId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );

  // group mutations
  const [editOrganizationGroupApiTrigger, { isLoading: isLoadingEditGroup }] =
    useEditOrganizationGroupMutation();
  const [
    editOrganizationGroupVehiclesApiTrigger,
    { isLoading: isLoadingEditGroupVehicles },
  ] = useEditOrganizationGroupVehiclesMutation();
  const [
    editOrganizationGroupUsersApiTrigger,
    { isLoading: isLoadingEditGroupUsers },
  ] = useEditOrganizationGroupUsersMutation();

  // fetch vehicles under the selected group
  const {
    data: dataSingleGroupVehicles,
    isFetching: isFetchingSingleGroupVehicles,
    isUninitialized: isUninitializedSingleGroupVehicles,
  } = useSingleOrganizationGroupVehiclesQuery({
    accountid: thisUserOrganizationId,
    groupid: groupId,
  } as SingleOrganizationGroupPayload);

  // fetch all organization vehicles
  const {
    data: dataOrgVehicles,
    isFetching: isFetchingOrgVehicles,
    isUninitialized: isUninitializedOrgVehicles,
  } = useOrganizationVehiclesFilterableQuery(
    {
      ...orgVehiclesQueryParams,
      // use assetSearchFields if defined at account level,
      // else default to APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE search fields
      search_fields:
        assetSearchFields.length > 0 ? assetSearchFields.join(",") : undefined, // if undefined, API will default to APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE search fields
    },
    {
      skip: isUninitializedSingleGroupVehicles || isFetchingSingleGroupVehicles,
    }
  );
  const { results: dataOrgVehicleResults } = dataOrgVehicles ?? {};

  // fetch users under the selected group
  const {
    data: dataSingleGroupUsers,
    isFetching: isFetchingSingleGroupUsers,
    isUninitialized: isUninitializedSingleGroupUsers,
  } = useSingleOrganizationGroupUsersQuery({
    accountid: thisUserOrganizationId,
    groupid: groupId,
  } as SingleOrganizationGroupPayload);

  // fetch available (not-in-group) users
  const {
    data: dataOrgUsers,
    isFetching: isFetchingOrgUsers,
    isUninitialized: isUninitializedOrgUsers,
  } = useOrganizationUsersFilterableQuery(orgUsersQueryParams, {
    skip: isUninitializedSingleGroupUsers || isFetchingSingleGroupUsers,
  });
  const { results: dataOrgUserResults } = dataOrgUsers ?? {};

  // on change of dataOrgVehicles, scroll list to top
  useEffect(() => {
    if (leftContainerRef.current) {
      leftContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [dataOrgVehicles]);

  // prepare all vehicle list
  const allVehicleList: TLegacyVehicle[] = !!dataOrgVehicleResults
    ? dataOrgVehicleResults
    : [];

  // prepare group vehicle list
  // -----------------------------------------------------------------------------------------------------------------
  // (11/7/2024) deprecated as this seems redundant, the dataSingleGroupVehicles already contains the list of vehicles
  // and we actually need to refresh the list of vehicles under the group when user switches from one group to another
  // -----------------------------------------------------------------------------------------------------------------
  // const groupVehicleList: TLegacyVehicle[] =
  //   !!dataSingleGroupVehicles
  //     ? dataSingleGroupVehicles
  //     : [];

  // updating description and vehicle list on data change
  useEffect(() => {
    if (isFetchingSingleGroup) return;
    if (!!dataSingleGroup && dataSingleGroup?.results?.length === 0) {
      // checking if dataSingleGroup?.results is empty, then navigate to the listing page
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          `Group not found, nav to listing >> ${JSON.stringify(dataSingleGroup)}`
        );
      navigate(routeUrls.dashboardChildren.adminChildren.groups);
    }
    console.log(
      "dataSingleGroup?.results?.[0]?.description",
      dataSingleGroup?.results?.[0]?.description
    );
    setCurrentDescription(dataSingleGroup?.results?.[0]?.description || "");
  }, [dataSingleGroup, isFetchingSingleGroup, dataSingleGroupVehicles]);

  // handle search change for right side
  // const handleRightSearchChange = (e: any) => {
  //   setRightSearchText(e.target.value);
  // }

  //handle cancel button
  const handleCancelButton = () => {
    setCurrentDescription(dataSingleGroup?.results?.[0]?.description ?? "");
    isNewEntity?.current ? setUserCanEdit(true) : setUserCanEdit(false);
    onCancel?.();
  };

  // Shared save: commits the group description, the full vehicle membership
  // and the full user membership in one go. Either dual-listbox's Save button
  // calls this, passing its own current list; the other list is read from the
  // parent state kept in sync by each listbox.
  const saveGroup = (
    currentVehicleList: TCustomizableItemList[],
    currentUserList: TCustomizableItemList[]
  ) => {
    const data: TEditOrganizationGroupPayloadData = {
      accountid: thisUserOrganizationId,
      groupid: groupId,
      description: currentDescription,
    };

    const dataVehicles: TEditOrganizationGroupVehiclesPayloadData = {
      accountid: thisUserOrganizationId,
      groupid: groupId,
      device_ids: currentVehicleList.map((vehicleItem) => vehicleItem.id).join(","),
    };

    const dataUsers: TEditOrganizationGroupUsersPayloadData = {
      accountid: thisUserOrganizationId,
      groupid: groupId,
      user_ids: currentUserList.map((userItem) => userItem.id).join(","),
    };

    // call api to update group: description -> vehicles -> users
    editOrganizationGroupApiTrigger({ data })
      .unwrap()
      .then(() => {
        editOrganizationGroupVehiclesApiTrigger({ data: dataVehicles })
          .unwrap()
          .then(() => {
            editOrganizationGroupUsersApiTrigger({ data: dataUsers })
              .unwrap()
              .then(() => {
                onUpdateSuccess();
              })
              .catch(onUpdateError);
          })
          .catch(onUpdateError);
      })
      .catch(onUpdateError);
  };

  // handle edit group (triggered from the vehicles listbox Save button)
  const handleEditGroup = (currentAllList: TCustomizableItemList[]) => {
    saveGroup(currentAllList, rightUserListItems);
  };

  // handle edit group users (triggered from the users listbox Save button)
  const handleEditGroupUsers = (currentUserList: TCustomizableItemList[]) => {
    saveGroup(rightListItems, currentUserList);
  };

  // Shared Save/Cancel for the whole form (both sections). The parent-owned
  // rightListItems / rightUserListItems are always the authoritative full
  // membership, so the shared Save reads them directly.
  const handleSharedSave = () => {
    saveGroup(rightListItems, rightUserListItems);
  };

  const handleSharedCancel = () => {
    // reset both lists to their last-loaded membership, then run the common
    // cancel behaviour (description reset + edit-mode handling + onCancel)
    setRightListItems(initialRightListData);
    setRightUserListItems(initialRightUserListData);
    handleCancelButton();
  };

  const leftContainerRef = React.useRef<HTMLDivElement>(null);
  const leftListData: TCustomizableItemList[] = !!allVehicleList
    ? allVehicleList.map((item) => {
        return {
          id: item.deviceid,
          title: consistentDisplayAssetItem(
            item.deviceid,
            item.description,
            item.displayname,
            item.drivername,
            showDriver
          ),
          // title: `${item.deviceid}${item.description ? ` | ${item.description}` : ""}${item.displayname ? ` | ${item.displayname}` : ""}`,
          sub_title: item.equipmenttype || "-",
          isactive: item.isactive,
        };
      })
    : [];

  // ---- Users: build a consistent display title for a user list item ----
  const displayUserItem = (user: TLegacyUser): string => {
    return [user.userid, user.description, user.displayname]
      .filter((part) => !!part)
      .join(" | ");
  };

  // prepare available (not-in-group) user list
  const allUserList: TLegacyUser[] = !!dataOrgUserResults
    ? dataOrgUserResults
    : [];

  const leftUserContainerRef = React.useRef<HTMLDivElement>(null);
  const leftUserListData: TCustomizableItemList[] = !!allUserList
    ? allUserList.map((item) => {
        return {
          id: item.userid,
          title: displayUserItem(item),
          sub_title: item.contactname || "-",
          isactive: item.isactive,
        };
      })
    : [];

  // scroll available users list to top when it changes
  useEffect(() => {
    if (leftUserContainerRef.current) {
      leftUserContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [dataOrgUsers]);

  // right list definition moved above

  // update initialRightListData and rightListItems when dataSingleGroupVehicles changes
  useEffect(() => {
    if (isFetchingSingleGroupVehicles) {
      setInitialRightListData([]);
      setRightListItems([]);
      setUserCanEdit(!!isNewEntity?.current ? true : false);
    } else {
      const newRightListItems = !!dataSingleGroupVehicles
        ? dataSingleGroupVehicles
            .filter((item) => !!item.deviceid)
            .map((item) => {
              return {
                id: item.deviceid,
                title: consistentDisplayAssetItem(
                  item.deviceid,
                  item.description,
                  item.displayname,
                  item.drivername,
                  showDriver
                ),
                //title: `${item.deviceid}${item.description ? ` | ${item.description}` : ""}${item.displayname ? ` | ${item.displayname}` : ""}`,
                sub_title: item.equipmenttype || "-",
                isactive: item.isactive,
              };
            })
        : [];
      setInitialRightListData(newRightListItems);
      setRightListItems(newRightListItems);
    }
  }, [dataSingleGroupVehicles, isFetchingSingleGroupVehicles]);

  // on rightListItems change, update API params via setOrgVehiclesQueryParams
  useEffect(() => {
    if (isFetchingSingleGroupVehicles) return;
    setOrgVehiclesQueryParams({
      ...orgVehiclesQueryParams,
      exclude_ids: rightListItems.map((item) => item.id),
    });
  }, [rightListItems, isFetchingSingleGroupVehicles]);

  // populate in-group users right list when group users data changes
  useEffect(() => {
    if (isFetchingSingleGroupUsers) {
      setInitialRightUserListData([]);
      setRightUserListItems([]);
    } else {
      const newRightUserListItems = !!dataSingleGroupUsers
        ? dataSingleGroupUsers
            .filter((item) => !!item.userid)
            .map((item) => {
              return {
                id: item.userid,
                title: displayUserItem(item),
                sub_title: item.contactname || "-",
                isactive: item.isactive,
              };
            })
        : [];
      setInitialRightUserListData(newRightUserListItems);
      setRightUserListItems(newRightUserListItems);
    }
  }, [dataSingleGroupUsers, isFetchingSingleGroupUsers]);

  // on rightUserListItems change, update available-users API params exclude_ids
  useEffect(() => {
    if (isFetchingSingleGroupUsers) return;
    setOrgUsersQueryParams({
      ...orgUsersQueryParams,
      exclude_ids: rightUserListItems.map((item) => item.id),
    });
  }, [rightUserListItems, isFetchingSingleGroupUsers]);

  // trigger loading state change callbacks
  useEffect(() => {
    onLoadingStateChangeEditGroup?.(isLoadingEditGroup);
  }, [isLoadingEditGroup]);
  useEffect(() => {
    onLoadingStateChangeSingleGroup?.(isFetchingSingleGroup);
  }, [isFetchingSingleGroup]);
  useEffect(() => {
    onLoadingStateChangeSingleGroupVehicles?.(isFetchingSingleGroupVehicles);
  }, [isFetchingSingleGroupVehicles]);

  if (
    isUninitializedOrgVehicles ||
    // || isFetchingOrgVehicles || isFetchingSingleGroupVehicles
    isFetchingSingleGroup ||
    isLoadingEditGroup ||
    isLoadingEditGroupVehicles ||
    isLoadingEditGroupUsers
  ) {
    return (
      <div className={`Xabsolute relative Xz-10 min-w-full h-full min-h-72`}>
        <img
          src={svgIcons.LoadingAnimation}
          alt="loading-animation"
          className="w-20 h-20 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        />
      </div>
    );
  }

  return (
    <>
      <div className="w-full grid grid-cols-12 gap-4">
        {showGroupIdField && (
          <AdminFormFieldInput
            customWrapperClass={
              viewOnlyMode && renderedOnModal ? "col-span-12" : "col-span-4"
            }
            label={t("group_id")}
            type="text"
            id="GroupId"
            name="group_id"
            value={groupId}
            onChange={(e) => {
              /* setCurrentDescription(e.target.value) */
            }}
            disabled={!userCanEdit}
            readOnly={true}
          />
        )}
        <AdminFormFieldInput
          customWrapperClass={
            (viewOnlyMode && renderedOnModal) || !showGroupIdField
              ? `col-span-12`
              : `col-span-8`
          }
          label={t("group_description")}
          type="text"
          id="GroupDescription"
          name="group_description"
          placeholder={t("group_description_placeholder")}
          value={
            currentDescription ?? dataSingleGroup?.results?.[0]?.description
          }
          onChange={(e) => {
            setCurrentDescription(e.target.value);
          }}
          disabled={!userCanEdit || viewOnlyMode}
        />
      </div>
      <div className="grid grid-cols-12 py-3 mb-6">
        <p
          className={`${viewOnlyMode && renderedOnModal ? "col-span-6" : "col-span-8"} self-center font-semibold text-xl leading-10 text-accent-blue-dark`}
        >
          {t("vehicles")}
        </p>
        <div
          className={
            viewOnlyMode && renderedOnModal ? "col-span-6" : "col-span-4"
          }
        >
          <AdminFormFieldCheckbox
            label={t("show_inactive_vehicles")}
            id="ShowInactiveVehicles"
            type="checkbox"
            name="show_inactive_vehicles"
            checked={showInactiveVehicles}
            onChange={(e) => {
              setShowInactiveVehicles(e.target.checked);
            }}
          />
        </div>
      </div>
      <div
        className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"} grid grid-cols-12 gap-4 relative ${isLoadingEditGroup || loadingState || isFetchingOrgVehicles || isFetchingSingleGroup || isFetchingSingleGroupVehicles ? "opacity-40" : ""}`}
      >
        {/* TODO: need to fix prefilling of vehicles */}
        {isUninitializedOrgVehicles || isFetchingSingleGroupVehicles ? (
          <div className={`absolute z-10 min-w-full h-full`}>
            <img
              src={svgIcons.LoadingAnimation}
              alt="loading-animation"
              className="w-20 h-20 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            />
          </div>
        ) : (
          <DualListboxPagedLHS
            leftListContainerRef={leftContainerRef}
            leftLoading={isFetchingOrgVehicles || isUninitializedOrgVehicles}
            leftTitle={t("not_in_selected_group")}
            leftSearchKeyword={orgVehiclesQueryParams.search}
            leftSearchPlaceholder={t("search_placeholder")}
            leftSearchCallback={(e: React.ChangeEvent<HTMLInputElement>) =>
              debouncedSetSearchKeyword(e.target.value)
            }
            leftList={leftListData}
            LeftPaginationComponent={
              <>
                {leftListData.length === 0 && (
                  <>
                    {/* <p className="text-[10px] text-gray-400 text-center mb-1 mx-2">{t("no_assets_found")}</p> */}
                  </>
                )}
                {leftListData.length > 0 && !showInactiveVehicles && (
                  <p className="text-[10px] text-gray-400 text-center mb-2 mx-2">
                    {t("some_inactive_assets_might_be_hidden")}
                  </p>
                )}
                {!!dataOrgVehicles?.count && (
                  <Pagination
                    pageSize={orgVehiclesQueryParams.page_size}
                    // handlePageSizeChange={(e: TSelectboxOption | null) => {
                    //   dispatch(setListingQueryParams({ ...listingQueryParams, groups: { ...orgGroupsQueryParams, page: 1, page_size: parseInt(`${e?.value}`) } }));
                    // }}
                    totalPages={
                      Math.ceil(
                        dataOrgVehicles?.count /
                          orgVehiclesQueryParams.page_size
                      ) || 0
                    }
                    forcePage={orgVehiclesQueryParams.page - 1}
                    handlePageClick={(data: TPaginationSelected) => {
                      // dispatch(setListingQueryParams({ ...listingQueryParams, groups: { ...orgGroupsQueryParams, page: data?.selected + 1 } }));
                      setOrgVehiclesQueryParams({
                        ...orgVehiclesQueryParams,
                        page: data?.selected + 1,
                      });
                    }}
                    onlyPageChange={true}
                    size={1}
                    wrapperClass="flex items-center justify-center"
                  />
                )}
              </>
            }
            disabled={!userCanEdit || viewOnlyMode}
            rightTitle={t("in_selected_group")}
            rightSearchPlaceholder={t("search_placeholder")}
            // rightSearchCallback={handleRightSearchChange} // disabled to let the default (within DualListbox component) search callback run
            initialRightList={initialRightListData}
            rightListItems={rightListItems}
            setRightListItems={setRightListItems}
            handleCancelButton={handleCancelButton}
            handleSaveButton={handleEditGroup}
            groupPage={true}
            showInactiveEntities={showInactiveVehicles}
            showInactiveEntitiesText={true}
            saveButtonText={tMain("save")}
            showStatusTagOnItems={true}
            displayRightSideOnly={viewOnlyMode && renderedOnModal} // on regular group page the edit button is disabled so we don't need this
            hideActionButtons={true}
          />
        )}
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Users section (mirrors the Vehicles section above)              */}
      {/* ---------------------------------------------------------------- */}
      <div className="grid grid-cols-12 py-3 mb-6">
        <p
          className={`${viewOnlyMode && renderedOnModal ? "col-span-6" : "col-span-8"} self-center font-semibold text-xl leading-10 text-accent-blue-dark`}
        >
          {t("users")}
        </p>
        <div
          className={
            viewOnlyMode && renderedOnModal ? "col-span-6" : "col-span-4"
          }
        >
          <AdminFormFieldCheckbox
            label={t("show_inactive_users")}
            id="ShowInactiveUsers"
            type="checkbox"
            name="show_inactive_users"
            checked={showInactiveUsers}
            onChange={(e) => {
              setShowInactiveUsers(e.target.checked);
            }}
          />
        </div>
      </div>
      <div
        className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"} grid grid-cols-12 gap-4 relative ${isLoadingEditGroup || loadingState || isFetchingOrgUsers || isFetchingSingleGroup || isFetchingSingleGroupUsers ? "opacity-40" : ""}`}
      >
        {isUninitializedOrgUsers || isFetchingSingleGroupUsers ? (
          <div className={`absolute z-10 min-w-full h-full`}>
            <img
              src={svgIcons.LoadingAnimation}
              alt="loading-animation"
              className="w-20 h-20 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            />
          </div>
        ) : (
          <DualListboxPagedLHS
            leftListContainerRef={leftUserContainerRef}
            leftLoading={isFetchingOrgUsers || isUninitializedOrgUsers}
            leftTitle={t("not_in_selected_group")}
            leftSearchKeyword={orgUsersQueryParams.search}
            leftSearchPlaceholder={t("search_placeholder_users")}
            leftSearchCallback={(e: React.ChangeEvent<HTMLInputElement>) =>
              debouncedSetUserSearchKeyword(e.target.value)
            }
            leftList={leftUserListData}
            LeftPaginationComponent={
              <>
                {leftUserListData.length > 0 && !showInactiveUsers && (
                  <p className="text-[10px] text-gray-400 text-center mb-2 mx-2">
                    {t("some_inactive_users_might_be_hidden")}
                  </p>
                )}
                {!!dataOrgUsers?.count && (
                  <Pagination
                    pageSize={orgUsersQueryParams.page_size}
                    totalPages={
                      Math.ceil(
                        dataOrgUsers?.count / orgUsersQueryParams.page_size
                      ) || 0
                    }
                    forcePage={orgUsersQueryParams.page - 1}
                    handlePageClick={(data: TPaginationSelected) => {
                      setOrgUsersQueryParams({
                        ...orgUsersQueryParams,
                        page: data?.selected + 1,
                      });
                    }}
                    onlyPageChange={true}
                    size={1}
                    wrapperClass="flex items-center justify-center"
                  />
                )}
              </>
            }
            disabled={!userCanEdit || viewOnlyMode}
            rightTitle={t("in_selected_group")}
            rightSearchPlaceholder={t("search_placeholder_users")}
            initialRightList={initialRightUserListData}
            rightListItems={rightUserListItems}
            setRightListItems={setRightUserListItems}
            handleCancelButton={handleCancelButton}
            handleSaveButton={handleEditGroupUsers}
            groupPage={true}
            showInactiveEntities={showInactiveUsers}
            showInactiveEntitiesText={true}
            saveButtonText={tMain("save")}
            showStatusTagOnItems={true}
            displayRightSideOnly={viewOnlyMode && renderedOnModal}
            hideActionButtons={true}
          />
        )}
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Shared Save / Cancel for the whole form (single save button).    */}
      {/* Shown wherever the per-listbox buttons used to appear, i.e. not   */}
      {/* in the read-only modal view (displayRightSideOnly).               */}
      {/* ---------------------------------------------------------------- */}
      {!(viewOnlyMode && renderedOnModal) && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <AdminFormFieldSubmit
            type="button"
            variant="primary"
            label={tMain("cancel")}
            onClick={handleSharedCancel}
            disabled={!userCanEdit || viewOnlyMode}
            customWrapperClass="w-32"
          />
          <AdminFormFieldSubmit
            type="button"
            variant="success"
            label={tMain("save")}
            onClick={handleSharedSave}
            disabled={!userCanEdit || viewOnlyMode}
            customWrapperClass="w-32"
          />
        </div>
      )}
    </>
  );
};
export default GroupEditFormFields;
